import { Reference, FieldPolicy } from "@apollo/client";

type KeyArgs = FieldPolicy<any>["keyArgs"];

type InternalCustomCursor<TNode> = Readonly<{
  cursor: string | null;
  hasMore: boolean;
  results: TNode[];
  previous?: string | null;
  hasPrevious?: boolean;
}>;

const makeEmptyData = (bidirectional: boolean) =>
  bidirectional
    ? {
        hasMore: true,
        hasPrevious: false,
        cursor: null,
        previous: null,
        results: [],
      }
    : {
        hasMore: true,
        cursor: null,
        results: [],
      };

const getDataFromArgs = (args) => ({
  cursor: args.cursor,
  previous: args.previous,
});

const customCursorPagination = <TNode = Reference>(
  keyArgs: KeyArgs = false,
  bidirectional = false,
  cursorsFromArgsFn = getDataFromArgs,
): FieldPolicy<InternalCustomCursor<TNode>> => ({
  keyArgs,
  merge(existing = makeEmptyData(bidirectional), incoming, { args, variables }) {
    const { cursor, previous } = cursorsFromArgsFn(args);
    if (!args) {
      return existing;
    }
    const shouldReplace = (variables as any)?.replaceResults;
    if (cursor && !shouldReplace) {
      return {
        ...existing,
        cursor: incoming.cursor,
        hasMore: incoming.hasMore,
        results: [...existing.results, ...incoming.results],
      };
    }
    if (previous && !shouldReplace) {
      return {
        ...existing,
        previous: incoming.previous,
        hasPrevious: incoming.hasPrevious,
        results: [...incoming.results, ...existing.results],
      };
    }
    return {
      ...incoming,
      results: [...incoming.results],
    };
  },
});

export default customCursorPagination;
